import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDesktopInboxToasts } from './useDesktopInboxToasts';
import { useAuth } from '../components/AuthProvider';
import { NOTIFICATION_TYPE } from '../notifications';
import { WorkspaceAPI, setActiveWorkspaceId } from '../utils/api';
import { recordInboxUndo, redoInboxAction, undoInboxAction } from '../utils/inboxUndoHistory';
import { hasUndoRecorder, recordUndoableAction } from '../utils/undoRecordBridge';
import {
  markInviteSeen,
  markInvitesSeen,
  loadSeenInviteIds,
  toInviteInboxItem,
} from '../utils/inviteInbox';
import {
  INBOX_CHANGED,
  INBOX_TARGETS_GONE,
  INBOX_TARGETS_RESTORED,
  clearInboxTargetsGone,
  loadInbox,
  markAllInboxRead,
  markInboxItemRead,
  markInboxTargetsGone,
  pullRemoteInbox,
  removeInboxItem,
  restoreInboxItem,
  unreadInboxCount,
} from '../utils/notificationInbox';

export function useNotificationInbox() {
  const { user, workspaceId, pendingInvites = [], refreshWorkspaces, refreshPendingInvites } = useAuth();
  const userEmail = user?.email || '';

  const [missionItems, setMissionItems] = useState(() => loadInbox(userEmail, workspaceId));
  const [seenInviteIds, setSeenInviteIds] = useState(() => loadSeenInviteIds(userEmail));
  const [inviteBusyId, setInviteBusyId] = useState(null);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    setSeenInviteIds(loadSeenInviteIds(userEmail));
  }, [userEmail]);

  useEffect(() => {
    setMissionItems(loadInbox(userEmail, workspaceId));
    let cancelled = false;
    pullRemoteInbox(userEmail, workspaceId).then((next) => {
      if (!cancelled && next) setMissionItems(next);
    });
    return () => {
      cancelled = true;
    };
  }, [userEmail, workspaceId]);

  useEffect(() => {
    const refresh = () => setMissionItems(loadInbox(userEmail, workspaceId));
    const onGone = (event) => setMissionItems(markInboxTargetsGone(userEmail, workspaceId, event.detail));
    const onRestored = (event) => setMissionItems(clearInboxTargetsGone(userEmail, workspaceId, event.detail));
    const pullFromServer = () => {
      pullRemoteInbox(userEmail, workspaceId).then((next) => {
        if (next) setMissionItems(next);
      });
      refreshPendingInvites?.();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') pullFromServer();
    };
    window.addEventListener(INBOX_CHANGED, refresh);
    window.addEventListener(INBOX_TARGETS_GONE, onGone);
    window.addEventListener(INBOX_TARGETS_RESTORED, onRestored);
    document.addEventListener('visibilitychange', onVisible);
    const poll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      pullFromServer();
    }, 60000);
    return () => {
      window.removeEventListener(INBOX_CHANGED, refresh);
      window.removeEventListener(INBOX_TARGETS_GONE, onGone);
      window.removeEventListener(INBOX_TARGETS_RESTORED, onRestored);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(poll);
    };
  }, [userEmail, workspaceId, refreshPendingInvites]);

  const inviteItems = useMemo(
    () => (pendingInvites || []).map((inv) => toInviteInboxItem(inv, seenInviteIds)),
    [pendingInvites, seenInviteIds]
  );

  const items = useMemo(() => {
    return [...inviteItems, ...missionItems].sort((a, b) =>
      String(b.createdAt || '').localeCompare(String(a.createdAt || ''))
    );
  }, [inviteItems, missionItems]);

  const markRead = useCallback(
    (id) => {
      const invite = inviteItems.find((item) => item.id === id);
      if (invite) {
        setSeenInviteIds(markInviteSeen(userEmail, id));
        return;
      }
      setMissionItems(markInboxItemRead(userEmail, workspaceId, id));
    },
    [inviteItems, userEmail, workspaceId]
  );

  const markAllRead = useCallback(() => {
    setSeenInviteIds(markInvitesSeen(userEmail, inviteItems.map((item) => item.id)));
    setMissionItems(markAllInboxRead(userEmail, workspaceId));
  }, [inviteItems, userEmail, workspaceId]);

  const deleteItem = useCallback(
    (item) => {
      if (!item?.id || item.type === NOTIFICATION_TYPE.WORKSPACE_INVITE) return;
      const { removed } = removeInboxItem(userEmail, workspaceId, item.id);
      if (!removed) return;
      setMissionItems(loadInbox(userEmail, workspaceId));
      const name = removed.missionName || removed.title || 'Notification';
      const action = {
        type: 'DELETE_NOTIFICATION',
        description: `Delete notification: ${name}`,
        undo: () => {
          setMissionItems(restoreInboxItem(userEmail, workspaceId, removed));
        },
        redo: () => {
          removeInboxItem(userEmail, workspaceId, removed.id);
          setMissionItems(loadInbox(userEmail, workspaceId));
        },
      };
      if (!recordUndoableAction(action)) recordInboxUndo(action);
    },
    [userEmail, workspaceId]
  );

  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (hasUndoRecorder()) return;
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const ctrlOrCmd = isMac ? event.metaKey : event.ctrlKey;
      if (!ctrlOrCmd || event.altKey) return;
      const redo = event.key === 'y' || (event.key === 'z' && event.shiftKey);
      const undo = event.key === 'z' && !event.shiftKey;
      if (!undo && !redo) return;
      const did = redo ? redoInboxAction() : undoInboxAction();
      if (!did) return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const dismissInvite = useCallback(
    (item) => {
      if (!item?.id) return;
      setInviteError('');
      setSeenInviteIds(markInviteSeen(userEmail, item.id));
    },
    [userEmail]
  );

  const acceptInvite = useCallback(
    async (item) => {
      if (!item?.workspaceId || !item?.inviteId || inviteBusyId) return;
      setInviteBusyId(item.id);
      setInviteError('');
      try {
        await WorkspaceAPI.acceptInvite(item.workspaceId, item.inviteId);
        setSeenInviteIds(markInviteSeen(userEmail, item.id));
        setActiveWorkspaceId(item.workspaceId);
        await refreshWorkspaces?.();
      } catch (err) {
        const already = (err?.message || '').toLowerCase().includes('already a member');
        if (already) {
          setSeenInviteIds(markInviteSeen(userEmail, item.id));
          setActiveWorkspaceId(item.workspaceId);
          await refreshWorkspaces?.();
          return;
        }
        setInviteError(err?.message || 'Could not accept this invite.');
      } finally {
        setInviteBusyId(null);
      }
    },
    [inviteBusyId, refreshWorkspaces, userEmail]
  );

  const unreadCount = useMemo(() => unreadInboxCount(items), [items]);

  useDesktopInboxToasts(items, markRead);

  return {
    items,
    unreadCount,
    markRead,
    markAllRead,
    deleteItem,
    acceptInvite,
    dismissInvite,
    inviteBusyId,
    inviteError,
    userEmail,
    workspaceId,
    INVITE_TYPE: NOTIFICATION_TYPE.WORKSPACE_INVITE,
  };
}
